/* ============================================
   QUOTE DASHBOARD — Claim Paperwork
   Asset: dashboard-claim-invoice.js

   CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-26)

   WHAT THIS IS
   A freight carrier will not pay a damage claim on our say-so. Ryan, on RPL-1003-A:
   "My shipping company requires that I send them an invoice for the damaged goods
   that we're submitting the damage claim for … Also, they need to see an invoice
   for what we charged our customer."

   So this panel produces two PDFs:

     THE CLAIM INVOICE   — addressed to the carrier, damaged goods only, priced at
       our vendor cost. Built from the REPLACEMENT's own picked items, so there is
       nothing to edit: it is whatever the admin picked as damaged.

     THE CUSTOMER INVOICE — the parent deal's real invoice, restated. The claim form
       and the invoice rarely speak the same language (sq ft on ours, pallets on
       theirs), so descriptions and quantities are editable here.
       ⛔ CHANGING A QUANTITY MOVES THE UNIT PRICE, NOT THE TOTAL. Ryan: "if I adjust
       a qty from 100 to 25, then it needs to multiply the price x4 since I divided
       the qty x4". The line total — and therefore the invoice total — is the
       invariant. An adjuster holding this next to the real invoice must not find a
       discrepancy we introduced for their convenience.

   ⛔ THE PARENT DEAL IS READ-ONLY TO THIS PANEL. Everything saves onto the
   REPLACEMENT's `lifecycle.claim.docs`. JW-1129-F's own invoice, its AR/AP card and
   its `invoiced` flag are untouched, and must stay that way — a carrier's preferred
   unit of measure cannot be allowed to restate a real customer invoice.

   ⛔ ADMIN-ONLY, and the worker agrees. Both /claim-docs routes refuse a non-admin
   with a 403 rather than relying on this card being hidden. Same reasoning as the
   recovery-claim card next to it: the claim is the company's money, not the rep's.

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE main.js.
   Public API on window.RQD:

     renderClaimDocsCard(q)  -> html string ('' when not applicable)
     claimDocs.handle(btn)   -> click delegation target for [data-claimdoc-action]

   Init contract (RQD.initClaimDocs, called once by main.js at boot):
     esc, money, formatDate — function declarations in main, safe by reference.
     getIsAdmin — closure-getter, NOT a bare reference. isAdmin is a `var` in main
       that mutates after Auth.getSession() resolves, so a bare reference captures
       false at init time and the whole card silently disappears for anyone who
       authenticates after boot. Same trap documented in dashboard-claim-card.js.
   ============================================ */
(function () {
  'use strict';
  window.RQD = window.RQD || {};

  var esc, money, formatDate, getIsAdmin;
  var API = null;
  function api() { if (!API) API = window.RepPortal && window.RepPortal.API; return API; }

  /* Per-ref state for the open panel. ⛔ Busy lives HERE, not as a disabled attribute on
     the button: the modal re-renders its own footer as the admin types, and a busy flag
     parked on a DOM node is lost the moment that node is replaced — which is how a second
     click gets through and generates the document twice. */
  var _state = {};

  window.RQD.initClaimDocs = function (deps) {
    esc = deps.esc;
    money = deps.money;
    formatDate = deps.formatDate;
    getIsAdmin = deps.getIsAdmin;
  };

  function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }

  /* The unit price as the PDF will print it: two decimals when qty x unit reconciles to
     the line total, four when it cannot. ⛔ This mirrors `_presUnitPriceStr` in the
     worker's pdf/invoice-pdf.js — if that rule changes, change it here too, or the editor
     will promise a number the document does not print. */
  function unitStr(unit, qty, lineTotal) {
    var two = round2(unit);
    if (Math.abs(round2(two * (Number(qty) || 0)) - round2(lineTotal)) < 0.005) return money(two);
    return '$' + (Number(unit) || 0).toFixed(4);
  }

  /* Which reasons mean a CARRIER owes us. Mirrors REPLACEMENT_REASONS in the worker's
     lib/replacement-order.js, the same contract dashboard-claim-card.js keeps for its
     labels — if a reason is added there, add it here. */
  var CARRIER_REASONS = { 'carrier-damage': true };

  /* ── The card: a thin launcher ─────────────────────────────────────────────
     Deliberately minimal, the same shape as the Adjust Order card. Everything live —
     the carrier address, the line editor, the totals, the already-generated PDFs — is
     fetched when the panel opens, so the card can never show a stale figure.

     ⛔ GATED ON `replacementReason`, NOT ON `claim.counterparty`. `lifecycle.claim` is
     never put on the row by quote-summary-builder — that builder's output is also what
     gets baked into the SHARED dd-summary cache, and the claim is admin-only, so it
     must stay out of there. It is attached instead at the response boundary, for admins
     only, by `attachClaimsForAdmin` in the worker's quote-dashboard.js (added 2026-08-26,
     which is also what fixed the recovery-claim card next door rendering its inputs
     blank after every save).

     So `q.claim` IS now populated for an admin — but this gate does not use it, on
     purpose. `replacementReason` is on the row for everyone, needs no claim record to
     exist yet, and answers the identical question, because the reason is what decides
     the counterparty in the first place (`counterpartyForReason` in the worker's
     lib/replacement-order.js). Gating on the derived field would make this card
     dependent on an admin-only enrichment for no gain. The worker re-checks the real
     counterparty before it will build anything either way. */
  function renderClaimDocsCard(q) {
    if (!q || !q.isReplacement) return '';
    if (!getIsAdmin()) return '';
    /* Only a carrier claim has a carrier to bill. A vendor defect, a warranty swap or a
       goodwill re-ship each have their own counterparty or none, and offering claim
       paperwork on those would imply somebody owes us for it. */
    if (!CARRIER_REASONS[q.replacementReason]) return '';

    var ref = q.reference;
    var html = '<div class="rqd-detail__card rqd-claimdocs-card">';
    html += '<div class="rqd-detail__section-title">Claim paperwork</div>';
    html += '<p class="rqd-claim-note">The two documents a carrier asks for before paying: a bill to them for the damaged goods at our cost, and the customer invoice showing what the order was worth.</p>';
    html += '<div class="rqd-claim-actions">' +
      '<button type="button" class="rqd-claim-save" data-claimdoc-action="open" data-ref="' + esc(ref) + '">Claim paperwork</button>' +
      '</div>';
    html += '</div>';
    return html;
  }

  /* ── Modal shell ───────────────────────────────────────────────────────────
     Reuses the Adjust Order modal's own classes rather than a hand-copied set. Those
     rules already carry the dark-mode overrides and the backdrop behaviour; a parallel
     copy would be one more surface to keep in step. */
  function closeModal() {
    var m = document.querySelector('.rqd-cd-modal-backdrop');
    if (m) m.remove();
    document.removeEventListener('keydown', _escClose);
  }
  function _escClose(e) { if (e.key === 'Escape') closeModal(); }

  function openModalShell(title) {
    closeModal();
    var back = document.createElement('div');
    /* Mounts on <body>, OUTSIDE .rep-quote-portal-wrap — which is where the --rq-* tokens
       are defined — so the dark state has to be mirrored explicitly. Hoisting out of the
       scope that defines your variables is a one-way trip; same note as the AO modal. */
    var isDark = !!document.querySelector('.rep-quote-portal-wrap.rep-quote-dark');
    back.className = 'rqd-ao-modal-backdrop rqd-cd-modal-backdrop' + (isDark ? ' rqd-ao-modal-backdrop--dark' : '');
    back.innerHTML =
      '<div class="rqd-ao-modal rqd-cd-modal" role="dialog" aria-modal="true">'
      + '<button type="button" class="rqd-ao-modal__x" aria-label="Close">&times;</button>'
      + '<div class="rqd-ao-modal__title">' + esc(title) + '</div>'
      + '<div class="rqd-ao-modal__body"></div>'
      + '</div>';
    document.body.appendChild(back);
    /* Mounted outside the table whose handler owns [data-claimdoc-action], so the modal
       delegates its own clicks or every button inside it is inert. */
    back.addEventListener('click', function (e) {
      var b = e.target.closest('[data-claimdoc-action]');
      if (b) { if (!b.disabled) handle(b); return; }
      if (window.RepPortal && window.RepPortal.dismissedOnBackdrop
        && window.RepPortal.dismissedOnBackdrop(e, back)) closeModal();
    });
    back.querySelector('.rqd-ao-modal__x').addEventListener('click', closeModal);
    document.addEventListener('keydown', _escClose);
    return back.querySelector('.rqd-ao-modal__body');
  }

  /* ── Panel ─────────────────────────────────────────────────────────────── */
  function openPanel(ref) {
    var body = openModalShell('Claim paperwork — ' + ref);
    body.innerHTML = '<div class="rqd-ao__loading">Loading…</div>';
    _state[ref] = { busy: false };

    api().post('/claim-docs/lines', { ref: ref }).then(function (res) {
      _state[ref].data = res;
      renderPanel(body, ref, res);
    }).catch(function (err) {
      /* ⛔ A failed load is not an empty state. Saying "no lines" over a request that
         never landed invites the admin to generate a document with nothing on it. */
      body.innerHTML = '<div class="rqd-cd-error">Could not load the claim paperwork.<br><span>'
        + esc(err.message || 'Unknown error') + '</span></div>';
    });
  }

  var BILL_FIELDS = [
    ['company', 'Company'],
    ['attn', 'Attn'],
    ['street', 'Street'],
    ['street2', 'Street 2'],
    ['city', 'City'],
    ['state', 'State'],
    ['zip', 'ZIP'],
  ];

  /* A link to whatever was generated last, shown inside the section it belongs to. Lives
     here rather than on the card because `claim.docs` — like the rest of the claim — never
     reaches the dashboard row; this panel's own fetch is the only place it is available. */
  function priorDoc(d, which) {
    var url = d.docs && d.docs[which + 'PdfUrl'];
    if (!url) return '';
    var when = d.docs[which + 'GeneratedAt'];
    return '<p class="rqd-cd-prior">Last built' + (when ? ' ' + esc(formatDate(when)) : '')
      + ': <a href="' + esc(url) + '" target="_blank" rel="noopener">open the PDF</a>'
      + ' <span>(links expire after 120 days — rebuild if it 404s)</span></p>';
  }

  /* The damaged-goods table for whichever branch of the carrier's form is selected. Both
     line sets arrive with the panel, so flipping the radio costs no round trip. */
  function renderClaimBody(body, d) {
    var host = body.querySelector('[data-cd-claimbody]');
    if (!host) return;
    var insured = isInsured(body);
    var lines = (insured ? d.retailLines : d.costLines) || [];
    if (!lines.length) {
      host.innerHTML = '<p class="rqd-cd-note rqd-cd-note--warn">No damaged items have been picked on this replacement yet. '
        + 'Choose them in the Workstation first — an empty claim bills the carrier for nothing.</p>';
      return;
    }
    var total = 0;
    var h = '<table class="rqd-cd-table"><thead><tr><th>Item</th><th class="num">Qty</th><th class="num">'
      + (insured ? 'Retail' : 'Cost') + '</th><th class="num">Total</th></tr></thead><tbody>';
    for (var a = 0; a < lines.length; a++) {
      var cl = lines[a];
      total += cl.lineTotal;
      h += '<tr><td>' + esc(cl.title) + '</td><td class="num">' + esc(String(cl.qty)) + '</td><td class="num">'
        + money(cl.unitPrice) + '</td><td class="num">' + money(cl.lineTotal) + '</td></tr>';
    }
    h += '</tbody></table>';
    h += '<div class="rqd-cd-total"><span>Claim total, at '
      + (insured ? 'the price the customer paid' : 'our vendor cost') + '</span><strong>' + money(total) + '</strong></div>';
    /* The claim card carries its own Amount claimed, entered by hand and independently.
       Saying the two disagree beats silently overwriting either one. */
    if (d.amountClaimed && Math.abs(d.amountClaimed - total) > 0.005) {
      h += '<p class="rqd-cd-note rqd-cd-note--warn">The claim card says ' + money(d.amountClaimed)
        + '. This invoice will say ' + money(total) + '.</p>';
    }
    host.innerHTML = h;
  }

  function isInsured(body) {
    var el = body.querySelector('[data-cd-insured="1"]');
    return !el || el.checked;
  }

  function renderPanel(body, ref, d) {
    var html = '';

    /* ── BILL TO ── */
    html += '<div class="rqd-cd-section">';
    html += '<div class="rqd-cd-section__title">Bill to — the carrier</div>';
    html += '<div class="rqd-cd-billto">';
    for (var i = 0; i < BILL_FIELDS.length; i++) {
      var f = BILL_FIELDS[i];
      html += '<label class="rqd-cd-field"><span>' + esc(f[1]) + '</span>'
        + '<input type="text" data-cd-bill="' + esc(f[0]) + '" value="' + esc(d.billTo[f[0]] || '') + '"></label>';
    }
    html += '</div>';
    if (d.claimNumber) {
      html += '<p class="rqd-cd-note">Claim #' + esc(d.claimNumber) + ' prints on the invoice, so the adjuster can file it without opening it.</p>';
    }
    html += '</div>';

    /* ── Doc A: the claim invoice ── */
    html += '<div class="rqd-cd-section">';
    html += '<div class="rqd-cd-section__title">1 — Manufacturer / Retail invoice</div>';
    /* The carrier's form asks for one or the other and says which: "Manufacturer/COGS Invoice
       (NON INSURED SHIPMENTS ONLY), OR, Provide Retail invoice if Falvey Insurance was used."
       We insure everything, so retail is the default and the uninsured branch is here only
       because their form has it. */
    html += '<div class="rqd-cd-basis">'
      + '<label><input type="radio" name="cd-insured" data-cd-insured="1"' + (d.insured ? ' checked' : '') + '>'
      + '<span><strong>Insured — retail</strong> ' + money(d.retailTotal) + '<em>Falvey covered it. Priced at what the customer paid.</em></span></label>'
      + '<label><input type="radio" name="cd-insured" data-cd-insured="0"' + (d.insured ? '' : ' checked') + '>'
      + '<span><strong>Not insured — COGS</strong> ' + money(d.costTotal) + '<em>Priced at our vendor cost.</em></span></label>'
      + '</div>';
    html += priorDoc(d, 'claim');
    html += '<div data-cd-claimbody></div>';
    if (d.claimLines.length) {
      html += '<div class="rqd-cd-actions">'
        + '<button type="button" class="rqd-claim-save" data-claimdoc-action="gen-claim" data-ref="' + esc(ref) + '">Generate this invoice</button>'
        + '<span class="rqd-cd-flag" data-cd-flag="claim" aria-live="polite"></span>'
        + '</div>';
    }
    html += '</div>';

    /* ── Doc B: the restated customer invoice ── */
    html += '<div class="rqd-cd-section">';
    html += '<div class="rqd-cd-section__title">2 — Customer invoice'
      + (d.parentRef ? ' — ' + esc(d.parentRef) : '') + '</div>';
    html += '<p class="rqd-cd-note">Their wording: <em>what you charged the customer, to show the value the item was sold for.</em></p>';
    html += priorDoc(d, 'customer');

    if (d.parentError) {
      html += '<div class="rqd-cd-error">Could not read the original order.<br><span>' + esc(d.parentError) + '</span></div>';
    } else if (!d.customerLines.length) {
      html += '<p class="rqd-cd-note">The original order has no line items to restate.</p>';
    } else {
      html += '<p class="rqd-cd-note">Rename anything and change any quantity to match the claim form. The price moves so the line total does not: the invoice still totals exactly what the customer was charged.</p>';
      html += '<table class="rqd-cd-table rqd-cd-table--edit"><thead><tr>'
        + '<th>Description</th><th class="num">Qty</th><th class="num">Price</th><th class="num">Total</th></tr></thead><tbody>';
      for (var b = 0; b < d.customerLines.length; b++) {
        var li = d.customerLines[b];
        var ov = d.lineOverrides[li.key] || {};
        var qty = ov.qty != null ? ov.qty : li.qty;
        var title = ov.title != null && ov.title !== '' ? ov.title : li.title;
        html += '<tr data-cd-row="' + esc(li.key) + '" data-cd-orig-total="' + esc(String(li.lineTotal)) + '"'
          + ' data-cd-orig-qty="' + esc(String(li.qty)) + '" data-cd-orig-title="' + esc(li.title) + '">'
          + '<td><input type="text" class="rqd-cd-desc" data-cd-field="title" value="' + esc(title) + '"></td>'
          + '<td class="num"><input type="number" min="1" step="1" class="rqd-cd-qty" data-cd-field="qty" value="' + esc(String(qty)) + '"></td>'
          + '<td class="num" data-cd-unit></td>'
          + '<td class="num" data-cd-linetotal></td>'
          + '</tr>';
      }
      html += '</tbody></table>';
      html += '<div class="rqd-cd-total"><span>Invoice total</span><strong data-cd-grand></strong>'
        + '<em class="rqd-cd-tie" data-cd-tie></em></div>';
      html += '<div class="rqd-cd-actions">'
        + '<button type="button" class="rqd-claim-save" data-claimdoc-action="gen-customer" data-ref="' + esc(ref) + '">Generate customer invoice</button>'
        + '<span class="rqd-cd-flag" data-cd-flag="customer" aria-live="polite"></span>'
        + '</div>';
    }
    html += '</div>';

    body.innerHTML = html;

    /* Recompute on input rather than re-rendering the table: a re-render would take the
       caret with it, and this is a form the admin types prose into. */
    body.addEventListener('input', function (e) {
      if (e.target.closest('[data-cd-field]')) recompute(body, d);
    });
    /* The radio fires `change`, not `input`, in every browser that matters. Only the claim
       table re-renders — the restatement editor below is untouched, so a half-typed
       description cannot be thrown away by flipping the insurance question. */
    body.addEventListener('change', function (e) {
      if (e.target.closest('[data-cd-insured]')) renderClaimBody(body, d);
    });
    renderClaimBody(body, d);
    recompute(body, d);
  }

  /* The whole invariant, in one place: the line total never moves, so the unit price is
     whatever it has to be. */
  function recompute(body, d) {
    var rows = body.querySelectorAll('[data-cd-row]');
    var grand = 0;
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var origTotal = parseFloat(row.getAttribute('data-cd-orig-total')) || 0;
      var origQty = parseInt(row.getAttribute('data-cd-orig-qty'), 10) || 1;
      var qtyEl = row.querySelector('[data-cd-field="qty"]');
      var qty = parseInt(qtyEl && qtyEl.value, 10);
      var bad = isNaN(qty) || qty <= 0;
      if (bad) qty = origQty;
      var unit = qty ? origTotal / qty : 0;
      row.classList.toggle('rqd-cd-row--bad', bad);
      row.querySelector('[data-cd-unit]').textContent = unitStr(unit, qty, origTotal);
      row.querySelector('[data-cd-linetotal]').textContent = money(origTotal);
      grand += origTotal;
    }
    var g = body.querySelector('[data-cd-grand]');
    if (g) g.textContent = money(grand);
    var tie = body.querySelector('[data-cd-tie]');
    if (tie) {
      /* Always true by construction — which is exactly why it is worth printing. The admin
         is about to hand this to an adjuster beside the real invoice, and the one question
         they need answered before they do is whether the totals still agree. */
      var matches = Math.abs(round2(grand) - round2(d.customerTotal)) < 0.005;
      tie.textContent = matches ? 'matches the original invoice' : 'DOES NOT match ' + money(d.customerTotal);
      tie.className = 'rqd-cd-tie' + (matches ? ' rqd-cd-tie--ok' : ' rqd-cd-tie--bad');
    }
  }

  function collect(body) {
    var billTo = {};
    body.querySelectorAll('[data-cd-bill]').forEach(function (el) {
      billTo[el.getAttribute('data-cd-bill')] = el.value.trim();
    });
    var lineOverrides = {};
    body.querySelectorAll('[data-cd-row]').forEach(function (row) {
      var key = row.getAttribute('data-cd-row');
      var origQty = parseInt(row.getAttribute('data-cd-orig-qty'), 10) || 1;
      var origTitle = row.getAttribute('data-cd-orig-title') || '';
      var titleEl = row.querySelector('[data-cd-field="title"]');
      var qtyEl = row.querySelector('[data-cd-field="qty"]');
      var title = (titleEl && titleEl.value.trim()) || '';
      var qty = parseInt(qtyEl && qtyEl.value, 10);
      var entry = {};
      /* Only send what actually differs. An override that restates a line as itself is
         noise in KV and, worse, pins the line against a future edit of the real order. */
      if (title && title !== origTitle) entry.title = title;
      if (!isNaN(qty) && qty > 0 && qty !== origQty) entry.qty = qty;
      if (Object.keys(entry).length) lineOverrides[key] = entry;
    });
    return { billTo: billTo, lineOverrides: lineOverrides, insured: isInsured(body) };
  }

  /* Open the PDF's tab NOW, while we are still inside the click.

     ⛔ `window.open` AFTER THE AWAIT IS A POPUP AND GETS BLOCKED. The URL does not exist until
     the worker has rendered the PDF and signed it, which is a round trip — and by the time
     that promise resolves the browser no longer counts this as user-initiated, so the tab is
     silently swallowed and the admin is left staring at a button that did nothing. The tab
     has to be claimed synchronously and pointed at the URL when it arrives.

     ⚠️ Returns null when a blocker refuses it outright. That is not an error — the caller
     falls back to the link in the status line, which is an ordinary anchor and always works. */
  function claimTab(label) {
    var win;
    try { win = window.open('', '_blank'); } catch (_) { return null; }
    if (!win) return null;
    try {
      /* Something to look at while the PDF renders — an about:blank tab reads as a crash.
         Styled inline: this document has no stylesheet of ours and never will. */
      win.document.write(
        '<!doctype html><title>' + label + '</title>'
        + '<body style="margin:0;display:flex;align-items:center;justify-content:center;'
        + 'height:100vh;font:16px/1.5 system-ui,sans-serif;color:#0c1f33;background:#f6f8fa">'
        + '<div style="text-align:center"><div style="font-weight:700">Building your ' + label + '…</div>'
        + '<div style="opacity:.6;font-size:14px;margin-top:6px">This tab will load the PDF when it is ready.</div></div>'
      );
      win.document.close();
    } catch (_) { /* cosmetic only */ }
    return win;
  }

  function generate(btn, doc) {
    var ref = btn.dataset.ref;
    var st = _state[ref] || (_state[ref] = {});
    if (st.busy) return;
    var body = btn.closest('.rqd-ao-modal__body');
    if (!body) return;
    var flag = body.querySelector('[data-cd-flag="' + doc + '"]');
    var payload = collect(body);

    /* Claimed BEFORE anything async — see claimTab. */
    var tab = claimTab(doc === 'claim' ? 'claim invoice' : 'customer invoice');

    st.busy = true;
    btn.disabled = true;
    if (flag) flag.textContent = 'Building…';

    api().post('/claim-docs/generate', {
      ref: ref, doc: doc, billTo: payload.billTo,
      lineOverrides: payload.lineOverrides, insured: payload.insured,
    }).then(function (res) {
      if (tab && !tab.closed) tab.location.href = res.pdfUrl;
      if (flag) {
        /* The link stays regardless of whether the tab opened: it is the fallback when a
           blocker refused the popup, and the way back after the tab is closed. */
        flag.innerHTML = '✓ ' + esc(money(res.total)) + ' — <a href="' + esc(res.pdfUrl)
          + '" target="_blank" rel="noopener">open PDF</a>'
          + (tab ? '' : ' <span class="rqd-cd-blocked">(pop-up blocked — use the link)</span>');
      }
      /* No dashboard refresh: nothing outside this panel displays claim data, and a quiet
         re-fetch mid-edit would be pure churn under an admin who is still typing. */
    }).catch(function (err) {
      /* ⛔ Close the tab we claimed. Leaving a "Building your invoice…" tab open on a request
         that failed is a document that never arrives, in a tab that looks like it is still
         working — the admin waits on it instead of reading the error. */
      if (tab && !tab.closed) { try { tab.close(); } catch (_) { /* already gone */ } }
      if (flag) flag.textContent = '';
      alert('Could not build the document: ' + (err.message || 'Unknown error'));
    }).finally(function () {
      st.busy = false;
      btn.disabled = false;
    });
  }

  function handle(btn) {
    var action = btn.getAttribute('data-claimdoc-action');
    if (action === 'open') return openPanel(btn.dataset.ref);
    if (action === 'gen-claim') return generate(btn, 'claim');
    if (action === 'gen-customer') return generate(btn, 'customer');
  }

  window.RQD.renderClaimDocsCard = renderClaimDocsCard;
  window.RQD.claimDocs = { handle: handle, close: closeModal };
})();
